// Phase 6 Part 2 (10/07) — pure helpers over the portfolio_digest daily series
// (job_reports docs, oldest → newest, as returned by
// /api/reports/portfolio-digest?days=N). Nothing here re-aggregates
// transactions; every number is a sum of what the job already stored.
//
//   buildTrendSeries(reports, {yesterday}) → weekly (or daily, when history is
//       shallow) points of funded / collected / bounced, plus the REAL range
//       covered so the chart can be labelled honestly — never back-filled.
//   weeklyCompare(reports, yesterday)      → this week-to-date vs the SAME
//       weekdays of last week (like-for-like), with arrows.
//
// (Named *Model to stay distinct from DigestTrends.js on case-insensitive
// file systems — Windows treats the two spellings as one file.)

const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : parseFloat(v) || 0);

// Dates are plain YYYY-MM-DD strings; we do the arithmetic in UTC at noon so
// local time zones never shift a day.
export const parseDay = (iso) => new Date(`${iso}T12:00:00Z`);
export const toIso = (d) => d.toISOString().slice(0, 10);
export const addDays = (iso, n) => {
  const d = parseDay(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
};
// Monday of the week containing `iso`.
export const weekStart = (iso) => {
  const d = parseDay(iso);
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - dow);
  return toIso(d);
};
export const shortDate = (iso) => {
  if (!iso) return '';
  return parseDay(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const countsOf = (doc) => ((doc && doc.data && doc.data.counts) || {});

const sumDocs = (docs) => docs.reduce((acc, doc) => {
  const c = countsOf(doc);
  acc.funded += num(c.funded_amount);
  acc.collected += num(c.collected);
  acc.bounced += num(c.bounced);
  acc.bounces += num(c.bounces);
  acc.days += 1;
  return acc;
}, { funded: 0, collected: 0, bounced: 0, bounces: 0, days: 0 });

// Keep docs with a usable date, deduped by date, oldest first.
const cleanReports = (reports) => {
  const byDate = new Map();
  (reports || []).forEach((r) => {
    const d = r && (r.date || (r.data && r.data.day));
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) byDate.set(d, { ...r, date: d });
  });
  return [...byDate.keys()].sort().map((k) => byDate.get(k));
};

export const buildTrendSeries = (reports, { months = 6, yesterday } = {}) => {
  const docs = cleanReports(reports);
  if (docs.length === 0) {
    return { points: [], granularity: 'week', range: null, shallow: true, rangeLabel: '' };
  }
  const last = docs[docs.length - 1].date;
  const end = yesterday && yesterday > last ? yesterday : last;
  // Window: `months` back from the end — only docs that exist inside it.
  const endD = parseDay(end);
  endD.setUTCMonth(endD.getUTCMonth() - months);
  const windowStart = toIso(endD);
  const inWindow = docs.filter((d) => d.date > windowStart);
  const first = inWindow[0].date;
  const lastDay = inWindow[inWindow.length - 1].date;
  const daysPresent = inWindow.length;
  const spanDays = Math.round((parseDay(lastDay) - parseDay(first)) / 86400000) + 1;
  const granularity = daysPresent <= 21 ? 'day' : 'week';

  let points;
  if (granularity === 'day') {
    points = inWindow.map((doc) => {
      const s = sumDocs([doc]);
      return { key: doc.date, label: shortDate(doc.date), ...s };
    });
  } else {
    const weeks = new Map();
    inWindow.forEach((doc) => {
      const w = weekStart(doc.date);
      if (!weeks.has(w)) weeks.set(w, []);
      weeks.get(w).push(doc);
    });
    points = [...weeks.keys()].sort().map((w) => {
      const s = sumDocs(weeks.get(w));
      return { key: w, label: shortDate(w), ...s, partial: s.days < 7 };
    });
  }

  return {
    points,
    granularity,
    range: { from: first, to: lastDay, daysPresent, spanDays, weeks: points.length },
    // "Shallow" = well short of the 6 months asked for; the chart must say so.
    shallow: spanDays < months * 28,
    rangeLabel: `${shortDate(first)} – ${shortDate(lastDay)} · ${daysPresent} day${daysPresent === 1 ? '' : 's'} of digest history`,
  };
};

export const trendArrow = (cur, prev) => (cur > prev ? 'up' : cur < prev ? 'down' : 'flat');

const pctChange = (cur, prev) => {
  if (!prev) return null; // no baseline → no percentage, never "+∞%"
  return ((cur - prev) / Math.abs(prev)) * 100;
};

export const weeklyCompare = (reports, yesterday) => {
  const docs = cleanReports(reports);
  if (docs.length === 0 || !yesterday) return null;
  const byDate = new Map(docs.map((d) => [d.date, d]));
  const thisFrom = weekStart(yesterday);
  const thisDays = [];
  for (let d = thisFrom; d <= yesterday; d = addDays(d, 1)) thisDays.push(d);
  const lastDays = thisDays.map((d) => addDays(d, -7));

  const pick = (days) => days.map((d) => byDate.get(d)).filter(Boolean);
  const cur = pick(thisDays);
  const prev = pick(lastDays);
  const curSum = sumDocs(cur);
  const prevSum = sumDocs(prev);

  const metric = (key, label, kind, higherIsBetter) => ({
    key, label, kind, higherIsBetter,
    current: curSum[key], previous: prevSum[key],
    delta: curSum[key] - prevSum[key],
    pct: pctChange(curSum[key], prevSum[key]),
    arrow: trendArrow(curSum[key], prevSum[key]),
  });

  return {
    thisWeek: { from: thisFrom, to: yesterday, daysExpected: thisDays.length, daysPresent: cur.length },
    lastWeek: { from: lastDays[0], to: lastDays[lastDays.length - 1], daysExpected: lastDays.length, daysPresent: prev.length },
    metrics: [
      metric('funded', 'Funded', 'money', true),
      metric('collected', 'Collected', 'money', true),
      metric('bounces', 'Bounces', 'count', false),
    ],
    // Only a like-for-like comparison is honest; say when a side is missing days.
    complete: cur.length === thisDays.length && prev.length === lastDays.length,
  };
};
